import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import Navbar from '../components/Navbar';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import { User } from '../interface/User';
import { getUsersWithoutMunicipality } from '../hooks/GetUserWithoutMunicipality';
import { getUsersWithinMunicipality } from '../hooks/GetUserWithinMunicipality';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import AddMunicipality from './AddMunicipality';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearMunicipality } from '../redux/municipialitySlice';
import { RootState } from '../store/store';
import { addMessage } from '../redux/infoMessageSlice';

const EditMunicipality: React.FC = () => {
  const auth: string = useAuthHeader() || '';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');
  const municipality = useSelector(
    (state: RootState) => state.municipality.selectedMunicipality
  );
  const [currentUser, setCurrentuser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersWithinMunicipality, setUsersWithinMunicipality] = useState<
    User[]
  >([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [userToRemove, setUserToRemove] = useState<User | null>(null);
  const [isUserToRemoveCurrentUser, setIsUserToRemoveCurrentUser] =
    useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [editRole, setEditRole] = useState('viewer');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { REACT_APP_API_URL } = process.env;
  const { t } = useTranslation();

  const availableRoles = ['viewer', 'editor', 'manager'];

  const selectedUserObj = users.find(
    (user) => user.id.toString() === selectedUserId
  );
  const isSelectedUserAdmin = selectedUserObj?.role === 'admin';
  const isUserToEditAdmin = userToEdit?.role === 'admin';

  const canModifyUser = (targetUserRole?: string) => {
    if (currentUser?.role === 'admin') return true;
    if (currentUser?.role === 'manager' && targetUserRole !== 'admin')
      return true;
    return false;
  };

  const fetchUsersWithoutMunicipality = async () => {
    try {
      const result = await getUsersWithoutMunicipality(auth, dispatch);
      if (Array.isArray(result)) {
        const mappedUsers = result.map((item) => ({
          id: item.user.id,
          email: item.user.email,
          username: item.user.username,
          role: item.role,
        }));
        setUsers(mappedUsers);
      } else {
        dispatch(
          addMessage({ message: 'error.failedToFetch', error: result.message })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  const fetchUsersWithinMunicipality = async () => {
    try {
      const result = await getUsersWithinMunicipality(auth, dispatch);
      if (Array.isArray(result)) {
        const username = localStorage.getItem('username');
        const currentUserData = result.find(
          (u) => u.user.username === username
        );

        if (currentUserData) {
          const userRole = currentUserData.role;

          if (userRole !== 'manager' && userRole !== 'admin') {
            dispatch(addMessage({ message: 'error.unauthorized' }));
            navigate('/');
            return;
          }

          setCurrentuser({ ...currentUserData.user, role: userRole });
        }

        const filteredUsers = result.filter(
          (user) => user.user.username !== username
        );
        const mappedUsers = filteredUsers.map((item) => ({
          id: item.user.id,
          email: item.user.email,
          username: item.user.username,
          role: item.role,
        }));
        setUsersWithinMunicipality(mappedUsers);
      } else {
        dispatch(
          addMessage({ message: 'error.failedToFetch', error: result.message })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  useEffect(() => {
    if (!auth) return;
    if (!municipality?.id) {
      navigate('/');
      return;
    }

    fetchUsersWithoutMunicipality();
    fetchUsersWithinMunicipality();
  }, [auth, municipality?.id, navigate]);

  const handleAddUserToMunicipality = async () => {
    if (!selectedUserId) return;

    try {
      const response = await fetch(
        `${REACT_APP_API_URL}/api/add_user_to_municipality/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
          },
          body: JSON.stringify({
            user_id: selectedUserId,
            municipality_id: municipality?.id,
            role: isSelectedUserAdmin ? 'viewer' : selectedRole,
          }),
        }
      );

      if (response.ok) {
        dispatch(
          addMessage({ message: 'municipality.userAdded', type: 'success' })
        );
        setSelectedUserId('');
        setSelectedRole('viewer');
        fetchUsersWithoutMunicipality();
        fetchUsersWithinMunicipality();
      } else {
        dispatch(
          addMessage({
            message: 'error.failedToFetch',
            error: response.statusText,
          })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  const handleLeaveMunicipality = () => {
    if (!currentUser) {
      return;
    }
    setIsUserToRemoveCurrentUser(true);
    handleOpenModal(currentUser);
  };

  const handleOpenModal = (user: User) => {
    if (!canModifyUser(user.role)) {
      dispatch(addMessage({ message: 'error.unauthorized' }));
      return;
    }
    setUserToRemove(user);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setUserToRemove(null);
  };

  const removeUserFromMunicipality = async (user: User) => {
    try {
      const response = await fetch(
        `${REACT_APP_API_URL}/api/remove_user_from_municipality/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
          },
          body: JSON.stringify({ user_id: user.id }),
        }
      );

      if (response.ok) {
        fetchUsersWithoutMunicipality();
        fetchUsersWithinMunicipality();
        if (isUserToRemoveCurrentUser) {
          setCurrentuser(null);
          setIsUserToRemoveCurrentUser(false);
          dispatch(clearMunicipality());
          navigate('/');
        }
        setModalOpen(false);
      } else {
        dispatch(
          addMessage({
            message: 'error.failedToFetch',
            error: response.statusText,
          })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  const handleRemoveUserFromMunicipality = async () => {
    if (!userToRemove) return;
    removeUserFromMunicipality(userToRemove);
  };

  const handleOpenEditRoleModal = (user: User) => {
    if (!canModifyUser(user.role)) {
      dispatch(addMessage({ message: 'error.unauthorized' }));
      return;
    }
    setUserToEdit(user);
    setEditRole(user.role || 'viewer');
    setEditRoleModalOpen(true);
  };

  const handleCloseEditRoleModal = () => {
    setEditRoleModalOpen(false);
    setUserToEdit(null);
  };

  const handleEditUserRole = async () => {
    if (!userToEdit) return;

    try {
      const response = await fetch(
        `${REACT_APP_API_URL}/api/edit_user_to_municipality/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
          },
          body: JSON.stringify({
            user_id: userToEdit.id,
            role: isUserToEditAdmin ? 'viewer' : editRole,
          }),
        }
      );

      if (response.ok) {
        fetchUsersWithinMunicipality();
        handleCloseEditRoleModal();
      } else {
        dispatch(
          addMessage({
            message: 'error.failedToFetch',
            error: response.statusText,
          })
        );
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    }
  };

  const handleOpenEditModal = () => {
    setEditModalOpen(true);
  };

  if (!currentUser) {
    return null;
  }

  return !editModalOpen ? (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Navbar />
      <Box
        sx={{
          width: '100%',
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            mt: isMobile ? '2vh' : '10vh',
            width: isMobile ? '95%' : '60%',
            height: isMobile ? '' : '60%',
            boxShadow: isMobile ? 0 : 12,
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: '1',
              width: '100%',
              display: 'flex',
              ml: 2,
              mt: 2,
            }}
          >
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.5rem', mr: 2 }}>
              {municipality?.name}
            </Typography>
            {municipality?.logo && (
              <img
                src={municipality.logo || ''}
                alt="Municipality Logo"
                style={{
                  height: 40,
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              width: '100%',
              height: isMobile ? 'auto' : '80%',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Box
              sx={{
                p: isMobile ? 0 : 2,
                m: 2,
                display: 'flex',
                flexDirection: 'column',
                width: isMobile ? '100%' : '50%',
              }}
            >
              <Typography component="h2" variant="h6">
                {t('municipality.userInMunicipality')}
              </Typography>
              <List
                id={'users-within-municipality-list'}
                sx={{ width: isMobile ? '90%' : '60%', maxWidth: 600, p: 0 }}
              >
                {usersWithinMunicipality.length === 0 && (
                  <Typography>{t('municipality.noUser')}</Typography>
                )}
                {usersWithinMunicipality.map((user) => (
                  <ListItem
                    sx={{
                      p: 0,
                      pt: 1,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                    key={user.id}
                  >
                    <ListItemText
                      primary={`${user.username} | ${
                        user.email
                      } | ${t(`municipality.${user.role}`)}`}
                      sx={{ wordBreak: 'break-word', m: 0 }}
                    />

                    {canModifyUser(user.role) && (
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleOpenEditRoleModal(user)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleOpenModal(user)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box
              sx={{
                p: isMobile ? 0 : 2,
                m: 2,
                display: 'flex',
                flexDirection: 'column',
                width: isMobile ? '100%' : '50%',
              }}
            >
              <Typography component="h2" variant="h6">
                {t('municipality.addUser')}
              </Typography>
              <Box
                id={'add-user-form'}
                component="form"
                onSubmit={(e) => e.preventDefault()}
                noValidate
                sx={{
                  width: isMobile ? '80%' : '60%',
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isMobile ? 'start' : 'center',
                  gap: 2,
                }}
              >
                <Autocomplete
                  fullWidth
                  id="user-autocomplete"
                  options={users}
                  getOptionLabel={(user) => `${user.username} | ${user.email}`}
                  value={selectedUserObj || null}
                  onChange={(event, newValue) => {
                    setSelectedUserId(newValue?.id.toString() || '');

                    if (newValue?.role === 'admin') {
                      setSelectedRole('admin');
                    } else if (selectedRole === 'admin') {
                      setSelectedRole('viewer');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('municipality.selectUser')}
                      placeholder={t('municipality.selectUser').toString()}
                      variant="outlined"
                    />
                  )}
                />
                <Autocomplete
                  fullWidth
                  disableClearable
                  id="role-autocomplete"
                  disabled={isSelectedUserAdmin}
                  options={isSelectedUserAdmin ? ['admin'] : availableRoles}
                  getOptionLabel={(option) => t(`municipality.${option}`)}
                  value={selectedRole}
                  onChange={(event, newValue) => {
                    setSelectedRole(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('municipality.selectRole')}
                      placeholder={t('municipality.selectRole').toString()}
                      variant="outlined"
                      required
                    />
                  )}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddUserToMunicipality}
                  disabled={!selectedUserId}
                  sx={{ alignSelf: 'start' }}
                >
                  {t('municipality.addUser')}
                </Button>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignSelf: isMobile ? 'center' : 'end',
              m: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenEditModal}
              sx={{ mr: 1 }}
            >
              {t('municipality.edit')}
            </Button>
            <Button
              id={'leave-municipality-button'}
              variant="contained"
              color="error"
              onClick={handleLeaveMunicipality}
              sx={{ mr: 1 }}
            >
              {t('municipality.leaveMunicipality')}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                navigate('/');
              }}
            >
              {t('municipality.back')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          id={'confirmation-window'}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '75%' : 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {isUserToRemoveCurrentUser
              ? t('municipality.leaveMunicipality')
              : t('municipality.removeUser')}
          </Typography>
          <Typography
            sx={{ mb: 4 }}
            dangerouslySetInnerHTML={{
              __html: isUserToRemoveCurrentUser
                ? t('municipality.confirmationTextCurrentUser', {
                    username: userToRemove?.username,
                  })
                : t('municipality.confirmationText', {
                    username: userToRemove?.username,
                  }),
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              id={'abort-button'}
              onClick={handleCloseModal}
            >
              {t('municipality.abort')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRemoveUserFromMunicipality}
            >
              {isUserToRemoveCurrentUser
                ? t('municipality.leave')
                : t('municipality.remove')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={editRoleModalOpen} onClose={handleCloseEditRoleModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '75%' : 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {t('municipality.editRole')}
          </Typography>
          <Typography sx={{ mb: 3 }}>{userToEdit?.username}</Typography>
          <Autocomplete
            fullWidth
            disableClearable
            disabled={isUserToEditAdmin}
            options={isUserToEditAdmin ? ['admin'] : availableRoles}
            getOptionLabel={(option) => t(`municipality.${option}`)}
            value={editRole}
            onChange={(event, newValue) => setEditRole(newValue)}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('municipality.selectRole')}
                variant="outlined"
                required
              />
            )}
            sx={{ mb: 4 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="contained" onClick={handleCloseEditRoleModal}>
              {t('municipality.abort')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditUserRole}
            >
              {t('municipality.save')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  ) : (
    <AddMunicipality
      closeFunction={() => {
        setEditModalOpen(false);
      }}
    />
  );
};

export default EditMunicipality;
